/**
 * Save schema migrations and light structural validation.
 * Never overwrite valid prior progress — migrate in place to the current version.
 */

import { defaultCharacterCombatStats } from '../domain/characterStats';
import type { DungeonRunState } from '../domain/dungeon';
import {
  sanitizeInventory,
  unequipItemsFailingClassLevelRequirement,
  type EquippedSlots,
  type ItemInstance,
} from '../domain/equipment';
import { normalizeEquipmentSnapshot } from '../domain/equipmentCombat';
import {
  createEmptyEquippedSlots,
  isEquipmentSlotId,
  isKnownItemDef,
  type ItemRarity,
} from '../data/equipment';
import {
  clampDungeonLevel,
  isDungeonMutationId,
  migrateLegacyDifficulty,
  type DungeonMutationId,
} from '../data/dungeonProgression';
import {
  deriveDiceBonusesFromGeneralRanks,
  normalizeGeneralTalentRanks,
  totalGeneralSpentFromRanks,
} from '../domain/generalTalents';
import {
  normalizeTalentRanks,
  totalSpentFromRanks,
} from '../domain/talents';
import {
  CLASS_LEVEL_CAP,
  GENERAL_LEVEL_EARNED_POINTS_CAP,
  GENERAL_MAX_TOTAL_POINTS,
  SAVE_SCHEMA_VERSION,
  createDefaultPermanentSave,
  wrapDungeonAsRun,
  type PermanentSave,
  type ProvisionalLootItem,
  type RunState,
  type SavedGame,
  type SavedGameV1,
} from './schema';

export class SaveMigrationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SaveMigrationError';
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readUnlockedSlots(raw: unknown, fallback: number): number {
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 1 && raw <= 6) {
    return Math.floor(raw);
  }
  return fallback;
}

function assertDungeonShape(dungeon: unknown): asserts dungeon is DungeonRunState {
  if (!isObject(dungeon)) {
    throw new SaveMigrationError('Save dungeon payload missing');
  }
  if (!isObject(dungeon.encounter)) {
    throw new SaveMigrationError('Save encounter payload missing');
  }
  if (typeof dungeon.dungeonId !== 'string') {
    throw new SaveMigrationError('Save dungeonId missing');
  }
}

function migratePermanentFromV1(v1: SavedGameV1): PermanentSave {
  const fromStub = v1.permanent?.unlockedDiceSlots;
  const fromDungeon = v1.dungeon?.unlockedDiceSlots;
  const unlockedDiceSlots = readUnlockedSlots(
    fromStub ?? fromDungeon,
    1,
  );
  return createDefaultPermanentSave({ unlockedDiceSlots });
}

/**
 * Milestone 4 → M5-A. Preserves dungeon + RNG; seeds empty permanent progression
 * except unlockedDiceSlots.
 */
export function migrateV1ToV2(v1: SavedGameV1): SavedGame {
  if (v1.version !== 1) {
    throw new SaveMigrationError(`Expected v1 save, got version ${String(v1.version)}`);
  }
  assertDungeonShape(v1.dungeon);
  if (typeof v1.rngState !== 'number' || !Number.isFinite(v1.rngState)) {
    throw new SaveMigrationError('Save rngState missing or invalid');
  }

  const permanent = migratePermanentFromV1(v1);
  const legacyDifficulty =
    (v1.dungeon as { difficulty?: string }).difficulty ?? 'normal';
  const progression = migrateLegacyDifficulty(legacyDifficulty);
  const dungeon: DungeonRunState = {
    ...v1.dungeon,
    unlockedDiceSlots: permanent.unlockedDiceSlots,
    permanent: { unlockedDiceSlots: permanent.unlockedDiceSlots },
    playLog: Array.isArray(v1.dungeon.playLog) ? v1.dungeon.playLog : [],
    playLogCursor:
      typeof v1.dungeon.playLogCursor === 'number' ? v1.dungeon.playLogCursor : 0,
    equipmentSnapshot: null,
    dungeonLevel: progression.dungeonLevel,
    mutation: progression.mutation,
  };

  const run = wrapDungeonAsRun(dungeon, {
    runId: `migrated_${dungeon.seed}_${v1.savedAt ?? 0}`,
  });

  return {
    version: SAVE_SCHEMA_VERSION,
    savedAt: typeof v1.savedAt === 'number' ? v1.savedAt : Date.now(),
    rngState: v1.rngState,
    permanent,
    run,
  };
}

function normalizeItemInstance(raw: unknown): ItemInstance | null {
  if (!isObject(raw)) return null;
  // M7-A ItemInstance shape
  if (typeof raw.instanceId === 'string' && typeof raw.defId === 'string') {
    if (!raw.instanceId || !raw.defId) return null;
    // Drop unknown defs safely (keep talents/XP; empty invalid gear refs).
    if (!isKnownItemDef(raw.defId)) return null;
    const rarity =
      typeof raw.rarity === 'string' && raw.rarity.length > 0
        ? (raw.rarity as ItemRarity)
        : 'common';
    return {
      instanceId: raw.instanceId,
      defId: raw.defId,
      rarity,
      acquiredAt:
        typeof raw.acquiredAt === 'number' ? raw.acquiredAt : Date.now(),
      source: typeof raw.source === 'string' ? raw.source : undefined,
      affixes: Array.isArray(raw.affixes)
        ? raw.affixes
            .filter(
              (a) =>
                isObject(a) &&
                typeof a.affixId === 'string' &&
                typeof a.value === 'number',
            )
            .map((a) => ({
              affixId: String((a as { affixId: string }).affixId),
              value: Math.floor(Number((a as { value: number }).value)),
            }))
        : undefined,
      itemLevel:
        typeof raw.itemLevel === 'number'
          ? Math.max(0, Math.floor(raw.itemLevel))
          : undefined,
    };
  }
  // Legacy OwnedEquipment `{ id, name }` — no defId. Brief: empty collection
  // for existing players; do not invent defs from display names.
  return null;
}

function normalizeEquipped(raw: unknown, items: ItemInstance[]): EquippedSlots {
  const equipped = createEmptyEquippedSlots();
  const ids = new Set(items.map((i) => i.instanceId));
  if (!isObject(raw)) {
    return equipped;
  }
  const used = new Set<string>();
  for (const [key, value] of Object.entries(raw)) {
    if (!isEquipmentSlotId(key)) continue;
    if (typeof value !== 'string' || !value) continue;
    if (!ids.has(value) || used.has(value)) continue;
    equipped[key] = value;
    used.add(value);
  }
  return equipped;
}

function normalizeProvisionalLootItem(raw: unknown): ProvisionalLootItem | null {
  if (!isObject(raw)) return null;
  const instanceId =
    typeof raw.instanceId === 'string'
      ? raw.instanceId
      : typeof raw.id === 'string'
        ? raw.id
        : null;
  const defId = typeof raw.defId === 'string' ? raw.defId : null;
  if (!instanceId || !defId || !isKnownItemDef(defId)) {
    // Legacy `{ id, name }` without defId cannot be banked as typed gear.
    return null;
  }
  return {
    instanceId,
    defId,
    rarity:
      typeof raw.rarity === 'string' ? (raw.rarity as ItemRarity) : undefined,
    source: typeof raw.source === 'string' ? raw.source : undefined,
    affixes: Array.isArray(raw.affixes)
      ? raw.affixes
          .filter(
            (a) =>
              isObject(a) &&
              typeof a.affixId === 'string' &&
              typeof a.value === 'number',
          )
          .map((a) => ({
            affixId: String((a as { affixId: string }).affixId),
            value: Math.floor(Number((a as { value: number }).value)),
          }))
      : undefined,
    itemLevel:
      typeof raw.itemLevel === 'number'
        ? Math.max(0, Math.floor(raw.itemLevel))
        : undefined,
  };
}

function normalizePermanent(raw: unknown): PermanentSave {
  const defaults = createDefaultPermanentSave();
  if (!isObject(raw)) {
    return defaults;
  }
  const unlockedDiceSlots = readUnlockedSlots(raw.unlockedDiceSlots, defaults.unlockedDiceSlots);
  const profile = isObject(raw.profile)
    ? {
        displayName:
          typeof raw.profile.displayName === 'string' || raw.profile.displayName === null
            ? (raw.profile.displayName as string | null)
            : null,
        activeClassId:
          raw.profile.activeClassId === 'vanguard' ? ('vanguard' as const) : 'vanguard',
      }
    : defaults.profile;

  const generalRaw = isObject(raw.general) ? raw.general : null;
  const generalTalentRanks = normalizeGeneralTalentRanks(
    generalRaw && 'talentRanks' in generalRaw ? generalRaw.talentRanks : {},
  );
  const generalSpentFromRanks = totalGeneralSpentFromRanks(generalTalentRanks);
  const generalLevel =
    generalRaw && typeof generalRaw.level === 'number'
      ? Math.max(1, Math.floor(generalRaw.level))
      : 1;
  const generalEarnedFromLevel = Math.min(
    GENERAL_LEVEL_EARNED_POINTS_CAP,
    Math.max(0, generalLevel - 1),
  );
  const generalEarnedRaw =
    generalRaw && typeof generalRaw.earnedPoints === 'number'
      ? Math.max(0, Math.floor(generalRaw.earnedPoints))
      : 0;
  const general = {
    xp:
      generalRaw && typeof generalRaw.xp === 'number'
        ? Math.max(0, generalRaw.xp)
        : 0,
    level: generalLevel,
    earnedPoints: Math.min(
      GENERAL_MAX_TOTAL_POINTS,
      Math.max(generalEarnedRaw, generalEarnedFromLevel),
    ),
    spentPoints:
      Object.keys(generalTalentRanks).length > 0
        ? generalSpentFromRanks
        : generalRaw && typeof generalRaw.spentPoints === 'number'
          ? Math.max(0, Math.floor(generalRaw.spentPoints))
          : 0,
    reservedMilestonePoints: 0,
    talentRanks: generalTalentRanks,
  };

  const classes = { ...defaults.classes };
  if (isObject(raw.classes) && isObject(raw.classes.vanguard)) {
    const v = raw.classes.vanguard;
    const talentRanks = normalizeTalentRanks(v.talentRanks);
    const spentFromRanks = totalSpentFromRanks(talentRanks);
    const spentPointsRaw =
      typeof v.spentPoints === 'number' ? Math.max(0, Math.floor(v.spentPoints)) : 0;
    // Prefer ranks as source of truth when present; otherwise keep spentPoints.
    const spentPoints =
      Object.keys(talentRanks).length > 0 ? spentFromRanks : spentPointsRaw;
    classes.vanguard = {
      xp: typeof v.xp === 'number' ? Math.max(0, v.xp) : 0,
      level: typeof v.level === 'number' ? Math.max(1, Math.floor(v.level)) : 1,
      earnedPoints:
        typeof v.earnedPoints === 'number' ? Math.max(0, Math.floor(v.earnedPoints)) : 0,
      spentPoints,
      reservedAchievementPoints:
        typeof v.reservedAchievementPoints === 'number'
          ? Math.max(0, Math.floor(v.reservedAchievementPoints))
          : 0,
      talentRanks,
    };
  }

  const currency = isObject(raw.currency)
    ? {
        gold: typeof raw.currency.gold === 'number' ? Math.max(0, raw.currency.gold) : 0,
        materials:
          typeof raw.currency.materials === 'number'
            ? Math.max(0, raw.currency.materials)
            : 0,
      }
    : defaults.currency;

  const parsedItems = Array.isArray(raw.equipment)
    ? raw.equipment
        .map(normalizeItemInstance)
        .filter((item): item is ItemInstance => item !== null)
    : [];
  const equipped = normalizeEquipped(raw.equipped, parsedItems);
  let sanitized = sanitizeInventory({ items: parsedItems, equipped });

  // M7-D2: unequip items that exceed the active class's Item Level requirement.
  // Items remain in the permanent collection — never deleted or rerolled.
  const activeClassId =
    profile.activeClassId === 'vanguard' ? 'vanguard' : 'vanguard';
  const activeClassLevel = Math.min(
    CLASS_LEVEL_CAP,
    Math.max(1, classes[activeClassId]?.level ?? 1),
  );
  const levelGate = unequipItemsFailingClassLevelRequirement(
    sanitized,
    activeClassLevel,
  );
  sanitized = levelGate.inventory;

  const dungeonCompletions: PermanentSave['dungeonCompletions'] = {};
  if (isObject(raw.dungeonCompletions)) {
    for (const [id, rec] of Object.entries(raw.dungeonCompletions)) {
      if (!isObject(rec)) continue;
      dungeonCompletions[id] = {
        clearCount:
          typeof rec.clearCount === 'number' ? Math.max(0, Math.floor(rec.clearCount)) : 0,
        lastClearedAt:
          typeof rec.lastClearedAt === 'number' ? rec.lastClearedAt : null,
      };
    }
  }

  const diceFromTalents = deriveDiceBonusesFromGeneralRanks(general.talentRanks);
  const specialDieSlotUnlocked =
    typeof raw.specialDieSlotUnlocked === 'boolean'
      ? raw.specialDieSlotUnlocked
      : diceFromTalents.specialDieSlotUnlocked;
  // Prefer talent-derived slots when General dice ranks exist; else keep legacy field.
  const slots =
    diceFromTalents.additionalDiceSlotRanks > 0
      ? diceFromTalents.unlockedDiceSlots
      : unlockedDiceSlots;

  return {
    profile,
    general,
    classes,
    equipment: sanitized.items,
    equipped: sanitized.equipped,
    currency,
    dungeonCompletions,
    unlockedDiceSlots: slots,
    specialDieSlotUnlocked,
  };
}

function normalizeRun(raw: unknown): RunState | null {
  if (raw === null) {
    return null;
  }
  if (!isObject(raw)) {
    throw new SaveMigrationError('Save run payload invalid');
  }
  assertDungeonShape(raw.dungeon);
  const status =
    raw.status === 'active' ||
    raw.status === 'awaiting_settlement' ||
    raw.status === 'settled'
      ? raw.status
      : 'active';
  const dungeon = raw.dungeon as DungeonRunState;
  const encounter = dungeon.encounter;
  const defaults = defaultCharacterCombatStats();
  const normalizedEncounter = {
    ...encounter,
    unlockedActiveSkillIds: Array.isArray(encounter.unlockedActiveSkillIds)
      ? encounter.unlockedActiveSkillIds.filter((id) => typeof id === 'string')
      : [],
    combatStats: {
      ...defaults,
      ...(encounter.combatStats ?? {}),
    },
    enemies: (encounter.enemies ?? []).map((enemy) => ({
      ...enemy,
      shield: typeof enemy.shield === 'number' ? enemy.shield : 0,
    })),
  };

  return {
    runId: typeof raw.runId === 'string' ? raw.runId : wrapDungeonAsRun(dungeon).runId,
    dungeonId:
      typeof raw.dungeonId === 'string'
        ? raw.dungeonId
        : dungeon.dungeonId,
    status,
    provisionalClassXp:
      typeof raw.provisionalClassXp === 'number'
        ? Math.max(0, raw.provisionalClassXp)
        : 0,
    provisionalLoot: Array.isArray(raw.provisionalLoot)
      ? raw.provisionalLoot
          .map(normalizeProvisionalLootItem)
          .filter((item): item is ProvisionalLootItem => item !== null)
      : [],
    settlement: isObject(raw.settlement)
      ? (raw.settlement as unknown as RunState['settlement'])
      : null,
    dungeon: {
      ...dungeon,
      encounter: normalizedEncounter,
      // Legacy difficulty kept for older readers; dungeonLevel + mutation are canonical.
      difficulty:
        dungeon.difficulty === 'hard' ||
        dungeon.difficulty === 'brutal' ||
        dungeon.difficulty === 'normal'
          ? dungeon.difficulty
          : dungeon.mutation === 'hard' || dungeon.mutation === 'brutal'
            ? dungeon.mutation
            : 'normal',
      dungeonLevel: (() => {
        if (typeof dungeon.dungeonLevel === 'number') {
          return clampDungeonLevel(dungeon.dungeonLevel);
        }
        const migrated = migrateLegacyDifficulty(dungeon.difficulty);
        return migrated.dungeonLevel;
      })(),
      mutation: ((): DungeonMutationId => {
        if (isDungeonMutationId(String(dungeon.mutation ?? ''))) {
          return dungeon.mutation as DungeonMutationId;
        }
        return migrateLegacyDifficulty(dungeon.difficulty).mutation;
      })(),
      equipmentLootAwardedEncounterIds: Array.isArray(
        dungeon.equipmentLootAwardedEncounterIds,
      )
        ? dungeon.equipmentLootAwardedEncounterIds.filter(
            (id) => typeof id === 'string',
          )
        : [],
      // Legacy active runs without a snapshot → null (unequipped combat; safe).
      equipmentSnapshot: normalizeEquipmentSnapshot(
        (dungeon as { equipmentSnapshot?: unknown }).equipmentSnapshot,
      ),
    },
  };
}

/**
 * Normalise a v2/v3 document (fill missing optional fields without wiping progress).
 */
export function normalizeV2(raw: Record<string, unknown>): SavedGame {
  if (typeof raw.rngState !== 'number' || !Number.isFinite(raw.rngState)) {
    throw new SaveMigrationError('Save rngState missing or invalid');
  }
  const permanent = normalizePermanent(raw.permanent);
  const run = normalizeRun(raw.run === undefined ? null : raw.run);
  if (run) {
    run.dungeon = {
      ...run.dungeon,
      unlockedDiceSlots: permanent.unlockedDiceSlots,
      permanent: { unlockedDiceSlots: permanent.unlockedDiceSlots },
    };
  }
  return {
    version: SAVE_SCHEMA_VERSION,
    savedAt: typeof raw.savedAt === 'number' ? raw.savedAt : Date.now(),
    rngState: raw.rngState,
    permanent,
    run,
  };
}

/** M7-D2: ensure dungeonLevel + mutation exist (from legacy difficulty if needed). */
export function migrateV2ToV3(raw: Record<string, unknown>): SavedGame {
  const base = normalizeV2(raw);
  return { ...base, version: 3 };
}

/**
 * M7-D2: Item Level class-equip requirements.
 * Applies unequip-to-bag policy for equipped items above the class level gate.
 * Does not delete, regenerate, or reroll items.
 */
export function migrateV3ToV4(raw: Record<string, unknown>): SavedGame {
  const base = normalizeV2(raw);
  return { ...base, version: 4 };
}

/**
 * Migrate any recognised on-disk payload to the current schema.
 * Throws SaveMigrationError when the payload is unrecoverable.
 */
export function migrateToCurrent(raw: unknown): SavedGame {
  if (!isObject(raw)) {
    throw new SaveMigrationError('Save root is not an object');
  }
  const version = raw.version;
  if (version === 1) {
    return migrateV1ToV2(raw as unknown as SavedGameV1);
  }
  if (version === 2) {
    return migrateV3ToV4(migrateV2ToV3(raw) as unknown as Record<string, unknown>);
  }
  if (version === 3) {
    return migrateV3ToV4(raw);
  }
  if (version === SAVE_SCHEMA_VERSION || version === 4) {
    return normalizeV2(raw);
  }
  throw new SaveMigrationError(`Unsupported save schema version: ${String(version)}`);
}
